(() => {
  const SECTION_TYPE = 'shoppable-stories';
  const instances = new Map();
  let activeInstance = null;

    const selectors = {
    wrap: '.ss-carousel-wrap',
    carousel: '[data-ss-carousel]',
    card: '[data-ss-card]',
    prev: '[data-ss-prev]',
    next: '[data-ss-next]',
    modal: '[data-ss-modal]',
    overlay: '[data-ss-overlay]',
    close: '[data-ss-close]',
    video: '[data-ss-video]',
    product: '[data-ss-product]',
    descWrap: '[data-ss-desc-wrap]',
    desc: '[data-ss-desc]',
    descToggle: '[data-ss-desc-toggle]'
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ShoppableStories {
    constructor(container) {
      this.container = container;
      this.root = container;
      this.carouselWrap = container.querySelector(selectors.wrap);

      this.carousel = this.root.querySelector(selectors.carousel);
      this.cards = Array.from(this.root.querySelectorAll(selectors.card));
      this.prevBtn = this.root.querySelector(selectors.prev);
      this.nextBtn = this.root.querySelector(selectors.next);
      this.modal = this.root.querySelector(selectors.modal);
      this.overlay = this.root.querySelector(selectors.overlay);
      this.closeBtn = this.root.querySelector(selectors.close);
      this.videoSlot = this.root.querySelector(selectors.video);
      this.productSlot = this.root.querySelector(selectors.product);

      this.bound = {
        onCardClick: this.onCardClick.bind(this),
        onPrevClick: this.onPrevClick.bind(this),
        onNextClick: this.onNextClick.bind(this),
        onOverlayClick: this.onOverlayClick.bind(this),
        onCloseClick: this.onCloseClick.bind(this),
        onAtcClick: this.onAtcClick.bind(this),
        onKeyDown: this.onKeyDown.bind(this),
        onScroll: this.onScroll.bind(this),
        onResize: this.onResize.bind(this)
      };

      this.isReady = true;
      this.init();
    }

    init() {
      this.cards.forEach((card) => card.addEventListener('click', this.bound.onCardClick));
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.bound.onPrevClick);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.bound.onNextClick);
      if (this.overlay) this.overlay.addEventListener('click', this.bound.onOverlayClick);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.bound.onCloseClick);
      this.root.addEventListener('click', this.bound.onAtcClick);
      if (this.modal) this.modal.addEventListener('keydown', this.bound.onKeyDown);
      if (this.carousel) this.carousel.addEventListener('scroll', this.bound.onScroll, { passive: true });
      window.addEventListener('resize', this.bound.onResize);
      this.initPreviewVideos();
      this.updateNavState();
    }

    destroy() {
      this.cards.forEach((card) => card.removeEventListener('click', this.bound.onCardClick));
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.bound.onPrevClick);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.bound.onNextClick);
      if (this.overlay) this.overlay.removeEventListener('click', this.bound.onOverlayClick);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.bound.onCloseClick);
      this.root.removeEventListener('click', this.bound.onAtcClick);
      if (this.modal) this.modal.removeEventListener('keydown', this.bound.onKeyDown);
      if (this.carousel) this.carousel.removeEventListener('scroll', this.bound.onScroll);
      window.removeEventListener('resize', this.bound.onResize);
      this.disconnectPreviewObserver();
      this.closeModal({ restoreFocus: false });
    }

    onPrevClick() {
      this.scrollBy(-1);
    }

    onNextClick() {
      this.scrollBy(1);
    }

    onScroll() {
      this.updateNavState();
    }

    onResize() {
      this.updateNavState();
    }

    scrollBy(direction) {
      if (!this.carousel) return;
      const card = this.carousel.querySelector(selectors.card);
      if (!card) return;
      const style = window.getComputedStyle(this.carousel);
      const gap = parseFloat(style.columnGap || style.gap || 0);
      const step = card.getBoundingClientRect().width + gap;
      this.carousel.scrollBy({
        left: direction * step,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    updateNavState() {
      if (!this.carousel || !this.prevBtn || !this.nextBtn) return;
      const maxScroll = this.carousel.scrollWidth - this.carousel.clientWidth - 2;
      const atStart = this.carousel.scrollLeft <= 2;
      const atEnd = this.carousel.scrollLeft >= maxScroll;
      const hasOverflow = this.carousel.scrollWidth > this.carousel.clientWidth + 2;

      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
      if (this.carouselWrap) {
        this.carouselWrap.classList.toggle('ss-nav-hidden', !hasOverflow);
      }
    }

    onCardClick(event) {
      const card = event.currentTarget;
      const videoHtml = card.dataset.videoHtml || '';
      const posterHtml = card.dataset.posterHtml || '';
      const productHtml = card.dataset.productHtml || '';
      const title = card.dataset.ssTitle || 'Story';
      this.openModal({ videoHtml, posterHtml, productHtml, title });
    }

    onOverlayClick(event) {
      if (event.target === this.overlay) {
        this.closeModal();
      }
    }

    onCloseClick() {
      this.closeModal();
    }

    onAtcClick(event) {
      const button = event.target.closest('.ss-atc-form .product-form__cart-submit');
      if (!button) return;
      if (!this.modal || this.modal.getAttribute('aria-hidden') === 'true') return;
      setTimeout(() => {
        this.closeModal();
      }, 300);
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openModal({ videoHtml, posterHtml, productHtml, title }) {
      if (!this.modal) return;

      if (activeInstance && activeInstance !== this) {
        activeInstance.closeModal({ restoreFocus: false });
      }
      activeInstance = this;
      this.lastFocused = document.activeElement;

      const videoMarkup = videoHtml || posterHtml;
      if (this.videoSlot) {
        this.videoSlot.innerHTML = videoMarkup || '';
      }
      if (this.productSlot) {
        this.productSlot.innerHTML = productHtml || '';
      }

      this.setupDescToggle();

      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.setAttribute('aria-label', title ? `Story: ${title}` : 'Story');
      this.lockScroll(true);

      const focusTarget = this.closeBtn || this.modal;
      if (focusTarget && focusTarget.focus) focusTarget.focus();

      this.playVideo();
    }

    setupDescToggle() {
      if (!this.productSlot) return;
      const wrap = this.productSlot.querySelector(selectors.descWrap);
      const desc = this.productSlot.querySelector(selectors.desc);
      const toggle = this.productSlot.querySelector(selectors.descToggle);
      if (!wrap || !desc || !toggle) return;

      const readMore = toggle.dataset.readMore || 'Read more';
      const readLess = toggle.dataset.readLess || 'Read less';
      toggle.textContent = readMore;
      toggle.hidden = true;

      requestAnimationFrame(() => {
        const needsToggle = desc.scrollHeight > desc.clientHeight + 4;
        toggle.hidden = !needsToggle;
      });

      toggle.addEventListener('click', () => {
        const expanded = wrap.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.textContent = expanded ? readLess : readMore;
      });
    }

    closeModal({ restoreFocus } = { restoreFocus: true }) {
      if (!this.modal || this.modal.getAttribute('aria-hidden') === 'true') return;
      this.stopVideo();
      this.modal.setAttribute('aria-hidden', 'true');
      if (this.videoSlot) this.videoSlot.innerHTML = '';
      if (this.productSlot) this.productSlot.innerHTML = '';
      this.lockScroll(false);
      if (restoreFocus && this.lastFocused && this.lastFocused.focus) {
        this.lastFocused.focus();
      }
      if (activeInstance === this) activeInstance = null;
    }

    playVideo() {
      if (!this.videoSlot) return;
      const video = this.videoSlot.querySelector('video');
      if (!video) return;
      video.muted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    initPreviewVideos() {
      this.previewVideos = Array.from(this.root.querySelectorAll('.ss-preview-video'));
      if (this.previewVideos.length === 0) return;

      this.previewVideos.forEach((video) => {
        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('loop', '');
        const tryPlay = () => {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
          }
        };
        tryPlay();
        video.addEventListener('loadeddata', tryPlay, { once: true });
      });

      if ('IntersectionObserver' in window) {
        this.previewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                const promise = video.play();
                if (promise && typeof promise.catch === 'function') {
                  promise.catch(() => {});
                }
              } else if (typeof video.pause === 'function') {
                video.pause();
              }
            });
          },
          { threshold: 0.25 }
        );
        this.previewVideos.forEach((video) => this.previewObserver.observe(video));
      }
    }

    disconnectPreviewObserver() {
      if (this.previewObserver) {
        this.previewObserver.disconnect();
        this.previewObserver = null;
      }
    }

    stopVideo() {
      if (!this.videoSlot) return;
      const video = this.videoSlot.querySelector('video');
      if (video && typeof video.pause === 'function') {
        video.pause();
      }
    }

    lockScroll(shouldLock) {
      if (shouldLock) {
        this.prevBodyOverflow = document.body.style.overflow;
        this.prevBodyPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.body.style.overflow = this.prevBodyOverflow || '';
        document.body.style.paddingRight = this.prevBodyPaddingRight || '';
      }
    }

    getFocusableElements() {
      if (!this.modal) return [];
      return Array.from(
        this.modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('aria-hidden'));
    }
  }

  const initContainer = (container) => {
    if (!container || instances.has(container)) return;
    const instance = new ShoppableStories(container);
    if (instance.isReady) instances.set(container, instance);
  };

  const destroyContainer = (container) => {
    const instance = instances.get(container);
    if (instance) {
      instance.destroy();
      instances.delete(container);
    }
  };

  const initAll = () => {
    document
      .querySelectorAll(`[data-section-type="${SECTION_TYPE}"]`)
      .forEach((container) => initContainer(container));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container =
      event.target.querySelector(`[data-section-type="${SECTION_TYPE}"]`) ||
      document.querySelector(
        `[data-section-id="${event.detail.sectionId}"][data-section-type="${SECTION_TYPE}"]`
      );
    initContainer(container);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = document.querySelector(
      `[data-section-id="${event.detail.sectionId}"][data-section-type="${SECTION_TYPE}"]`
    );
    destroyContainer(container);
  });
})();
